import { useEffect, useLayoutEffect, useState } from 'react';
import { useVoiceVisualizer, VoiceVisualizer } from 'react-voice-visualizer';
import { CronoVoiceVisualizerWrapper } from './style';
import { ReactComponent as PlayIcon } from 'crono-fe-common/icons/Icon-Play.svg';
import { ReactComponent as PauseIcon } from 'crono-fe-common/icons/Icon-Pause.svg';
import CheckMarkIcon from 'crono-fe-common/icons/Icon-CheckMark';
import { ReactComponent as DotIcon } from 'crono-fe-common/icons/Icon-Dot.svg';
import { colors } from 'crono-fe-common/theme';
import { base64ToBlobAsync } from 'utils/uiHelpers';
import { useConditionalSnackBar } from 'context/snackbar';
import { CircularProgress, Typography } from '@mui/material';
import MicrophoneIcon from 'crono-fe-common/icons/Icon-Microphone';
import useGetTemplateRecording from 'hooks/services/templates/useGetTemplateRecording';
import { getError } from 'crono-fe-common/utils';
import { cn } from 'utils/style-utils';
import TrashCanDeleteIcon from 'crono-fe-common/icons/Icon-Trash-Can';

interface IProps {
  exportRecording?: (recording: Blob | null) => void;
  exportDuration?: (duration: number) => void;
  allowToRemove?: boolean;
  disableRemove?: boolean;
  onRemove?: () => void;
  onRecordingStateChange?: (isRecording: boolean) => void;
  size?: 'small' | 'medium';

  // for saved recordings
  duration?: number;
  templateId?: number;
}

const CronoVoiceVisualizer = ({
  exportDuration,
  exportRecording,
  allowToRemove = true,
  disableRemove = false,
  onRemove,
  onRecordingStateChange,
  size = 'medium',

  duration,
  templateId,
}: IProps) => {
  const recorderControls = useVoiceVisualizer();

  const {
    isRecordingInProgress,
    recordingTime,
    duration: durationReal,
    audioRef,
    startRecording,
    stopRecording,
    recordedBlob,
    togglePauseResume,
    clearCanvas,
    setPreloadedAudioBlob,
    startAudioPlayback,
    isAvailableRecordedAudio,
  } = recorderControls;

  useEffect(() => {
    if (exportDuration) {
      exportDuration(durationReal);
    }
  }, [durationReal]);

  useEffect(() => {
    if (exportRecording) {
      exportRecording(recordedBlob);
    }
  }, [recordedBlob]);

  useEffect(() => {
    onRecordingStateChange?.(isRecordingInProgress);
  }, [isRecordingInProgress]);

  // Clear the canvas when user change template
  useEffect(() => {
    clearCanvas();
  }, [templateId]);

  const [recordingTimeLimitError, setRecordingTimeLimitError] =
    useState<boolean>(false);

  // Auto-stop recording after 59 seconds
  useEffect(() => {
    let t: NodeJS.Timeout;
    if (recordingTime / 1000 > 59) {
      stopRecording();
      setRecordingTimeLimitError(true);
      t = setTimeout(() => {
        setRecordingTimeLimitError(false);
      }, 3000);
    }
    return () => {
      if (t) clearTimeout(t);
    };
  }, [recordingTime, stopRecording]);

  const [savedDuration, setSavedDuration] = useState<number | undefined>(
    duration,
  );
  useEffect(() => {
    setSavedDuration(duration);
  }, [duration]);
  const durationToUse = savedDuration ?? durationReal;

  const {
    mutateAsync: getTemplateRecording,
    isLoading: isGetTemplateRecordingLoading,
    error: getTemplateRecordingError,
  } = useGetTemplateRecording();

  const fetchAndPlayRecording = async () => {
    if (!templateId) return;
    const response = await getTemplateRecording({ templateId });
    if (!response) return;
    const base64 = response.data.data;
    if (!base64) return;
    const blob = await base64ToBlobAsync(base64);
    setPreloadedAudioBlob(blob);
    startAudioPlayback();
  };

  useConditionalSnackBar([
    {
      condition: recordingTimeLimitError,
      message: 'The recording stopped due to 1 min limit',
      severity: 'error',
    },
    {
      condition: !!getTemplateRecordingError,
      message:
        getError(getTemplateRecordingError) ??
        'Error when loading the recording',
      severity: 'error',
    },
  ]);

  const formatRecordingTime = (milliseconds: number) => {
    // Convert milliseconds to seconds
    const totalSeconds = Math.floor(milliseconds / 1000);

    // Calculate minutes and seconds
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    // Add leading zeros if necessary
    const formattedSeconds = seconds < 10 ? '0' + seconds : seconds;

    // Return the formatted time
    return minutes + ':' + formattedSeconds;
  };

  //We need to set canvasWidth manually otherwise during first render it gets 0 width and no way for the wave to be seend while recording.
  //This way we set it when the state of the recorder changes, updating it manually to the correct width
  //When the audio is cleared set it back to 0 for correct visualization of the other buttons
  const [canvasWidth, setCanvasWidth] = useState<number>(0);

  const startRecordingMode =
    !isRecordingInProgress && !durationToUse && startRecording !== undefined;

  const showPlaceholder =
    !isRecordingInProgress && !audioRef.current && !startRecordingMode;

  useLayoutEffect(() => {
    if (!durationToUse && !isRecordingInProgress) {
      setCanvasWidth(0);
      return;
    }
    const elements = document.getElementsByClassName('voice-visualizer');
    if (!elements || elements.length === 0) return;
    const visualizer = elements[0];
    const width = visualizer.scrollWidth;
    setCanvasWidth(width);
  }, [isRecordingInProgress, durationToUse, showPlaceholder]);

  const renderRemoveButton =
    (isRecordingInProgress || !!durationToUse) && allowToRemove;
  const renderSaveButton = isRecordingInProgress;
  const renderRightControls = renderRemoveButton || renderSaveButton;

  return (
    <CronoVoiceVisualizerWrapper>
      <div
        className={cn('controls-container', {
          'start-recording-theme': startRecordingMode,
          'size-small': size === 'small',
        })}
        onClick={(e) => {
          e.stopPropagation();
          if (startRecordingMode) {
            startRecording();
          }
        }}
      >
        <div className="left-controls">
          {startRecordingMode && (
            <div className={'recorder-button'}>
              <div className={'icon-container'}>
                <MicrophoneIcon size={18} />
              </div>

              <Typography fontSize={14} lineHeight={'24px'} fontWeight={500}>
                Start recording
              </Typography>
            </div>
          )}

          {!!durationToUse && (
            <button
              className={'circle-button play-pause-button'}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (isGetTemplateRecordingLoading) return;
                if (templateId && !isAvailableRecordedAudio) {
                  fetchAndPlayRecording();
                } else {
                  togglePauseResume();
                }
              }}
            >
              {isGetTemplateRecordingLoading ? (
                <CircularProgress size={18} style={{ color: 'white' }} />
              ) : audioRef.current?.paused || !isAvailableRecordedAudio ? (
                <PlayIcon />
              ) : (
                <PauseIcon />
              )}
            </button>
          )}

          {isRecordingInProgress && (
            <div className={'dot-icon-container'}>
              <DotIcon />
            </div>
          )}

          {(isRecordingInProgress || !!durationToUse) && (
            <p className={isRecordingInProgress ? 'recording-time' : ''}>
              {isRecordingInProgress
                ? 'Recording: ' + formatRecordingTime(recordingTime)
                : formatRecordingTime(durationToUse * 1000)}
            </p>
          )}
        </div>

        <div className="visualizer-container">
          {showPlaceholder ? (
            <VoiceRecordingPlaceholder className={'voice-waves-container'} />
          ) : (
            <VoiceVisualizer
              key={canvasWidth}
              controls={recorderControls}
              canvasContainerClassName={'voice-waves-container'}
              barWidth={4}
              gap={1}
              rounded={4}
              height={40}
              fullscreen={true}
              //Width has to be passed manually to the library
              width={canvasWidth}
              mainBarColor={
                isRecordingInProgress
                  ? recordingTime / 1000 < 55
                    ? colors.primary
                    : colors.inactive
                  : colors.primaryLightHover
              }
              secondaryBarColor={
                isRecordingInProgress ? colors.inactive : colors.primary
              }
              isDefaultUIShown={false}
              isControlPanelShown={false}
              isProgressIndicatorShown={false}
              isProgressIndicatorOnHoverShown={false}
            />
          )}
        </div>

        {renderRightControls && (
          <div className="right-controls">
            {renderRemoveButton && (
              <button
                className={'circle-button delete-button'}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  clearCanvas();
                  setSavedDuration(undefined);
                  if (onRemove) {
                    onRemove();
                  }
                }}
                disabled={disableRemove}
              >
                <TrashCanDeleteIcon color={colors.inactive} />
              </button>
            )}

            {renderSaveButton && (
              <button
                className={'circle-button save-button'}
                onClick={stopRecording}
              >
                <CheckMarkIcon color={colors.callScheduled} />
              </button>
            )}
          </div>
        )}
      </div>
    </CronoVoiceVisualizerWrapper>
  );
};

interface IVoiceRecordingPlaceholderProps {
  className?: string;
}
const VoiceRecordingPlaceholder = ({
  className,
}: IVoiceRecordingPlaceholderProps) => {
  const containerStyle = {
    backgroundImage: 'url(/backgrounds/Icon-Voice-Recording-Wave.svg)',
    backgroundRepeat: 'repeat-x',
    width: '100%',
    height: '20px',
  };

  return <div style={containerStyle} className={className}></div>;
};

export default CronoVoiceVisualizer;
